import { useState } from "react";
import { Modal as AntModal } from "antd";
import { ArrowLeftOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Badge } from "@/components/ui";
import type { EmailTemplate } from "../types";
import { defaultEmailHtmlContent } from "../types";

interface TemplatesModalProps {
  open: boolean;
  templates: EmailTemplate[];
  onClose: () => void;
  onSelectTemplate: (template: EmailTemplate) => void;
}

const TemplatesModal = ({
  open,
  templates,
  onClose,
  onSelectTemplate,
}: TemplatesModalProps) => {
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);

  const handlePreview = (template: EmailTemplate) => {
    setPreviewTemplate(template);
  };

  const handleBack = () => {
    setPreviewTemplate(null);
  };

  const handleUseTemplate = (template: EmailTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  const handleClose = () => {
    setPreviewTemplate(null);
    onClose();
  };

  // Template Preview View
  if (previewTemplate) {
    return (
      <AntModal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={800}
        centered
        closable={false}
        className="templates-modal"
      >
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeftOutlined />
              </button>
              <span className="text-lg font-medium">{previewTemplate.name}</span>
              <Badge variant="info">Template</Badge>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Preview Content */}
          <div className="py-6 max-h-[60vh] overflow-y-auto">
            <div className="bg-gray-100 p-4 rounded-lg">
              <iframe
                srcDoc={previewTemplate.htmlContent || defaultEmailHtmlContent}
                title="Email Preview"
                className="w-full h-[400px] bg-white rounded border-0"
                sandbox="allow-same-origin"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
            <Button variant="primary" onClick={() => handleUseTemplate(previewTemplate)}>
              Use Template
            </Button>
          </div>
        </div>
      </AntModal>
    );
  }

  // Templates Gallery View
  return (
    <AntModal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={1100}
      centered
      title="Templates"
      className="templates-modal"
    >
      <div className="grid grid-cols-4 gap-4 py-4 max-h-[70vh] overflow-y-auto">
        {templates.map((template) => (
          <div
            key={template.id}
            className="border rounded-lg overflow-hidden bg-white hover:shadow-md transition-shadow"
          >
            {/* Template Name */}
            <div className="p-3 border-b">
              <h4 className="text-sm font-medium text-gray-900 truncate">
                {template.name}
              </h4>
            </div>

            {/* Template Thumbnail */}
            <div className="h-32 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center relative overflow-hidden">
              <div className="text-center px-4">
                <div className="inline-block border border-white/50 rounded-full px-3 py-1 mb-2">
                  <span className="text-white/80 text-xs font-medium">PLACEHOLDER</span>
                </div>
                <p className="text-white/60 text-xs">YOU ARE INVITED</p>
                <p className="text-white/40 text-[10px] mt-1">Hi {"{{attendeeFullName}}"},</p>
              </div>
            </div>

            {/* Actions */}
            <div className="p-3 flex gap-2">
              <Button
                variant="secondary"
                size="small"
                onClick={() => handlePreview(template)}
                block
              >
                Preview
              </Button>
              <Button
                variant="primary"
                size="small"
                onClick={() => handleUseTemplate(template)}
                block
              >
                Use
              </Button>
            </div>
          </div>
        ))}
      </div>
    </AntModal>
  );
};

export default TemplatesModal;
